import type { ComponentType } from 'react'
import dynamic from 'next/dynamic'
import { CORE_TEMPLATE_SECTION_TYPE } from '@models'
import { IntroSection } from '@components/entities/IntroSection'
import { ForceAny } from '@utils/typescript'

/**
 * Keep above-the-fold banner static for LCP; code-split every other CMS section
 * so unused Swiper/Motion/Document/form chunks stay off the critical path.
 */
export const ComponentTypeToComponentMapping: Record<
  CORE_TEMPLATE_SECTION_TYPE,
  ComponentType<ForceAny>
> = {
  [CORE_TEMPLATE_SECTION_TYPE.BANNER_SECTION]: IntroSection,
  [CORE_TEMPLATE_SECTION_TYPE.VIDEO_SECTION]: dynamic(() =>
    import('@components/feature/common/VideoSection').then(
      (m) => m.VideoSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.BRAND_SHOWCASE_SECTION]: dynamic(() =>
    import('@components/feature/common/BrandShowcase').then(
      (m) => m.BrandShowCase
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.TESTIMONIAL_SECTION]: dynamic(() =>
    import('@components/feature/common/TestimonialSlideSection').then(
      (m) => m.TestimonialSlideSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.ADVERTISEMENT_BANNER_SECTION]: dynamic(() =>
    import('@components/feature/common/AdvertisementBanner').then(
      (m) => m.AdvertisementBanner
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.PORTFOLIO_SECTION]: dynamic(() =>
    import('@components/feature/common/PortfolioSection').then(
      (m) => m.PortfolioSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.QNA_SECTION]: dynamic(() =>
    import('@components/feature/common/FaqSection').then((m) => m.FaqSection)
  ),
  [CORE_TEMPLATE_SECTION_TYPE.OUR_IMPACT_SECTION]: dynamic(() =>
    import('@components/feature/common/OurImpact').then((m) => m.OurImpact)
  ),
  [CORE_TEMPLATE_SECTION_TYPE.COMPARISON_SECTION]: dynamic(() =>
    import('@components/feature/common/ComparisonSection').then(
      (m) => m.ComparisonSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.FILTERABLE_CASE_STUDY_SECTION]: dynamic(() =>
    import('@components/feature/common/ProjectSection').then(
      (m) => m.ProjectSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.MULTI_FEATURE_SECTION]: dynamic(() =>
    import('@components/feature/common/MultiFeatureSection').then(
      (m) => m.MultiFeatureSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.MAP_SECTION]: dynamic(() =>
    import('@components/feature/common/MapSection').then((m) => m.MapSection)
  ),
  [CORE_TEMPLATE_SECTION_TYPE.DOCUMENT_SECTION]: dynamic(() =>
    import('@components/feature/common/DocumentSection').then(
      (m) => m.DocumentSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.RATING_STATS_SECTION]: dynamic(() =>
    import('@components/feature/common/RatingStatsSection').then(
      (m) => m.RatingStatsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.CERTIFICATION_SECTION]: dynamic(() =>
    import('@components/feature/common/CertificationSection').then(
      (m) => m.CertificationSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.MISSION_VISION_SECTION]: dynamic(() =>
    import('@components/feature/common/MissionVisionSection').then(
      (m) => m.MissionVisionSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.IMAGE_GALLERY_SECTION]: dynamic(() =>
    import('@components/feature/common/ImageGallerySection').then(
      (m) => m.ImageGallerySection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.HIGHLIGHT_SECTION]: dynamic(() =>
    import('@components/feature/common/HighlightSection').then(
      (m) => m.HighlightSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.DOCUMENT_BANNER]: dynamic(() =>
    import('@components/feature/common/DocumentBanner').then(
      (m) => m.DocumentBanner
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.ANIMATED_DOCUMENT_BANNER]: dynamic(() =>
    import('@components/feature/common/AnimatedDocumentBanner').then(
      (m) => m.AnimatedDcoumentBanner
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.REVIEWS_SECTION]: dynamic(() =>
    import('@components/feature/common/ReviewsSection').then(
      (m) => m.ReviewsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.FILTERABLE_STACKING_CARDS_SECTION]: dynamic(() =>
    import('@components/feature/common/TwoLevelCategorizedCardsSection').then(
      (m) => m.TwoLevelCategorizedCardsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.SCROLLABLE_MULTI_FEATURE_SECTION]: dynamic(() =>
    import('@components/feature/common/ScrollableMultiFeatureSection').then(
      (m) => m.ScrollableMultiFeatureSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.COMPARISON_TABLE_SECTION]: dynamic(() =>
    import('@components/feature/common/ComparisonTableSection').then(
      (m) => m.ComparisonTableSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.COMPARISON_CHIPS_SECTION]: dynamic(() =>
    import('@components/feature/common/ComparisonChipsSection').then(
      (m) => m.ComparisonChipsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.CONTACT_US_SECTION]: dynamic(() =>
    import('@components/feature/common/ContactUsSection').then(
      (m) => m.ContactUsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.FILTERABLE_STANDARD_SECTION]: dynamic(() =>
    import('@components/feature/common/FilterableStandardSection').then(
      (m) => m.FilterableStandardSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.RIGHT_ALIGN_ACCORDIONS_SECTION]: dynamic(() =>
    import('@components/feature/common/RightAlignAccordionsSection').then(
      (m) => m.RightAlignAccordionsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.CHIP_SHOWCASE_SECTION]: dynamic(() =>
    import('@components/feature/common/ChipShowcaseSection').then(
      (m) => m.ChipShowcaseSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.PRICING_STATS_CARDS_SECTION]: dynamic(() =>
    import('@components/feature/common/PricingStatsCardsSection').then(
      (m) => m.PricingStatsCardsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.PRODUCT_PRICING_CARDS_SECTION]: dynamic(() =>
    import('@components/feature/common/ProductPricingCardsSection').then(
      (m) => m.ProductPricingCardsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.STANDARD_PRICING_CARDS_SECTION]: dynamic(() =>
    import('@components/feature/common/StandardPricingCardsSection').then(
      (m) => m.StandardPricingCardsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.SMART_NAVIGATION_CARDS_SECTION]: dynamic(() =>
    import('@components/feature/common/SmartNavigationCardsSection').then(
      (m) => m.SmartNavigationCardsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.SERVICES_MULTI_TABS_SECTION]: dynamic(() =>
    import('@components/feature/common/ServicesMultiTabsSection').then(
      (m) => m.ServicesMultiTabsSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.OVERLAY_COMPARISON_SECTION]: dynamic(() =>
    import('@components/feature/common/OverlayComparisonSection').then(
      (m) => m.OverlayComparisonSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.HIRE_EXPERT_SECTION]: dynamic(() =>
    import('@components/feature/common/HireExpertSection').then(
      (m) => m.HireExpertSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.CHECK_ICON_TABLE_SECTION]: dynamic(() =>
    import('@components/feature/common/CheckIconTableSection').then(
      (m) => m.CheckIconTableSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.SCROLLABLE_RESOURCES_SLIDE_SECTION]: dynamic(() =>
    import('@components/feature/common/ScrollableResourcesSlideSection').then(
      (m) => m.ScrollableResourcesSlideSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.RELATED_RESOURCES_SECTION]: dynamic(() =>
    import('@components/feature/common/RecentBlogs').then((m) => m.RecentBlogs)
  ),
  [CORE_TEMPLATE_SECTION_TYPE.RELATED_BLOGS_SECTION]: dynamic(() =>
    import('@components/feature/common/RelatedCardSection').then(
      (m) => m.RelatedCardSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.ADVERTISEMENT_CAROUSEL_SECTION]: dynamic(() =>
    import('@components/feature/common/AdvertisementCarouselSection').then(
      (m) => m.AdvertisementCarouselSection
    )
  ),
  [CORE_TEMPLATE_SECTION_TYPE.FILTERABLE_ADVERTISEMENT_BANNER_SECTION]: dynamic(
    () =>
      import(
        '@components/feature/common/FilterableAdvertisementBannerSection'
      ).then((m) => m.FilterableAdvertisementBannerSection)
  ),
  [CORE_TEMPLATE_SECTION_TYPE.FILTERABLE_PAGINATED_TESTIMONIAL_SECTION]:
    dynamic(() =>
      import(
        '@components/feature/common/FilterablePaginatedTestimonialSection'
      ).then((m) => m.FilterablePaginatedTestimonialSection)
    ),
  [CORE_TEMPLATE_SECTION_TYPE.SCROLLABLE_CERTIFICATION_SECTION]: dynamic(() =>
    import('@components/feature/common/ScrollableCertificationSection').then(
      (m) => m.ScrollableCertificationSection
    )
  ),
}
